// SLA Monitor shared TypeScript types — derived from api-contract.md
// (see tcp-api/claude-agent-output-81/api-contract.md — SOURCE OF TRUTH)

// api-contract §1.1 — full internal status set.
export type SlaStatus =
  | "on_time"
  | "upcoming"
  | "at_risk"
  | "breached"
  | "ahead"
  | "no_sla"
  | "closed";

// api-contract §1.2/§1.3 — lifecycle-scoped status subsets & ordered pill lists.
export type OpenSlaStatus = "upcoming" | "at_risk" | "breached";
export type CompletedSlaStatus = "ahead" | "on_time" | "at_risk" | "breached";
export type SlaSectionType = "open" | "completed";

export type DateRangePreset =
  | "this_month"
  | "this_quarter"
  | "this_financial_year"
  | "last_month"
  | "last_quarter"
  | "last_financial_year"
  | "month_to_date"
  | "quarter_to_date"
  | "year_to_date";

// api-contract D0.6 — server default changes from "all" to "open"; "all" is
// deprecated and no longer selectable from the UI (react-frontend-tasks F1.3
// [Corrects breakdown]).
export type TaskLifecycleStatus = "open" | "completed";

export interface SlaFilters {
  start_date: string | null;
  end_date: string | null;
  team_member_ids: string[];
  property_id: string | null;
  task_lifecycle_status?: TaskLifecycleStatus; // echoed from backend
}

// api-contract §5.2 — lifecycle = "open" summary shape.
export interface OpenSlaSummary {
  lifecycle: "open";
  total_tasks: number;
  upcoming_count: number;
  at_risk_count: number;
  breached_count: number;
  compliance_rate: number;
  upcoming_pct: number;
  at_risk_pct: number;
  breached_pct: number;
}

// api-contract §5.3 — lifecycle = "completed" summary shape.
export interface CompletedSlaSummary {
  lifecycle: "completed";
  total_tasks: number;
  ahead_count: number;
  on_time_count: number;
  at_risk_count: number;
  breached_count: number;
  compliance_rate: number;
  ahead_pct: number;
  on_time_pct: number;
  at_risk_pct: number;
  breached_pct: number;
}

// Discriminated union on `lifecycle` — consumers must branch on
// `summary.lifecycle` before reading counts/percentages (api-contract §5.4).
export type SlaSummary = OpenSlaSummary | CompletedSlaSummary;

// api-contract §5.2 — per-property row for lifecycle = "open".
export interface OpenSlaPropertyRow {
  property_id: string;
  property_name: string;
  property_locality: string | null;
  total_tasks: number;
  upcoming_count: number;
  at_risk_count: number;
  breached_count: number;
  compliance_rate: number;
}

// api-contract §5.3 — per-property row for lifecycle = "completed".
export interface CompletedSlaPropertyRow {
  property_id: string;
  property_name: string;
  property_locality: string | null;
  total_tasks: number;
  ahead_count: number;
  on_time_count: number;
  at_risk_count: number;
  breached_count: number;
  compliance_rate: number;
}

// Property rows have no discriminant field of their own — the lifecycle is
// carried by the sibling `SlaSummary.lifecycle` on the same `SlaDashboardSummary`
// payload. Use `isOpenPropertyRow` below to narrow.
export type SlaPropertyRow = OpenSlaPropertyRow | CompletedSlaPropertyRow;

/** Narrows a `SlaPropertyRow` to `OpenSlaPropertyRow` (has `upcoming_count`). */
export function isOpenPropertyRow(row: SlaPropertyRow): row is OpenSlaPropertyRow {
  return "upcoming_count" in row;
}

export interface SlaDashboardSummary {
  filters: SlaFilters;
  summary: SlaSummary;
  properties: SlaPropertyRow[];
}

export interface SlaAssignee {
  id: string;
  firstname: string;
  surname: string;
  avatar: string | null;
}

export interface SlaTaskListItem {
  id: string;
  name: string;
  sla_status: SlaStatus;
  lifecycle_status: "open" | "completed"; // api-contract §3.3
  due_at: string;
  days_remaining: number;
  project_id: string;
  project_name: string;
  property_id: string;
  property_name: string;
  phase: string | null;
  assignees: SlaAssignee[];
  responsible_role_name: string | null;
  sla_target_days: number | null; // api-contract §3.3 — NEW
  sla_breached_days: number | null;
}

export interface EscalationRule {
  rule_key: string;
  enabled: boolean;
  label: string;
}

export interface SlaTaskDetail extends SlaTaskListItem {
  project_full_name: string | null;
  submitted_date: string | null;
  description: string | null;
  priority: string | null;
  // Resolved Upcoming window (task's own sla_target_days, else company default).
  // Used to compute the Upcoming date range in the detail modal.
  sla_effective_target_days: number | null;
  escalation_rules: EscalationRule[];
}

export interface SlaConfigurationData {
  id: string;
  company_id: string;
  upcoming_window_days: number;
  email_assignee_on_upcoming: boolean;
  reminder_on_at_risk: boolean;
  escalate_manager_on_at_risk: boolean;
  escalate_portfolio_on_breached: boolean;
  escalate_executive_after_24h: boolean;
  send_daily_digest: boolean;
  updated_at: string;
}

export interface SlaActiveFilters {
  dateRangePreset: DateRangePreset | null;
  startDate: string | null;
  endDate: string | null;
  teamMemberIds: string[];
  // slaStatus intentionally removed — per-section status is owned independently
  // inside TasksRequiringAttention (api-contract D0.5 / react-frontend-tasks F4).
  taskLifecycleStatus: TaskLifecycleStatus; // api-contract D0.6; default "open"
}
